import "@fortawesome/fontawesome-free/css/all.min.css";
import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentProfile from "@/pages/student/StudentProfile";
import StudentDocuments from "@/pages/student/StudentDocuments";
import BranchDashboard from "@/pages/branch/BranchDashboard";
import RegisterStudent from "@/pages/branch/RegisterStudent";
import StudentList from "@/pages/branch/StudentList";
import VerificationQueue from "@/pages/branch/VerificationQueue";
import VerificationView from "@/pages/branch/VerificationView";
import BranchAudit from "@/pages/branch/BranchAudit";
import StaffManagement from "@/pages/branch/StaffManagement";
import BranchAnalytics from "@/pages/branch/BranchAnalytics";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import BranchManagement from "@/pages/admin/BranchManagement";
import Analytics from "@/pages/admin/Analytics";
import AdminAudit from "@/pages/admin/AdminAudit";
import UserManagement from "@/pages/admin/UserManagement";
import StudentChat from "@/pages/student/StudentChat";
import ExpenseManager from "@/pages/branch/ExpenseManager";
import RegistrationWorkspace from "@/pages/branch/RegistrationWorkspace";
import CutoffExplorer from "@/pages/CutoffExplorer";
import PollPage from "@/pages/PollPage";
import UpdatesPage from "@/pages/UpdatesPage";
import MhtCetForm from "@/pages/MhtCetForm";

import { ServerWakeupProvider } from "@/contexts/ServerWakeupContext";
import { PublicAuthProvider, usePublicAuth } from "@/contexts/PublicAuthContext";
import { PublicAdminAuthProvider, usePublicAdmin } from "@/contexts/PublicAdminAuthContext";
import PublicLayout from "@/components/layouts/PublicLayout";
import PublicAdminLayout from "@/components/layouts/PublicAdminLayout";
import PublicHome from "@/pages/public/PublicHome";
import GuestLayout from "@/components/layouts/GuestLayout";
import GuestPoll from "@/pages/public/GuestPoll";
import PublicChats from "@/pages/public/PublicChats";
import PublicPredictor from "@/pages/public/PublicPredictor";
import GuestPredictorGate from "@/pages/public/GuestPredictorGate";
import PublicCutoffs from "@/pages/public/PublicCutoffs";
import PublicProfile from "@/pages/public/PublicProfile";
import CollegeExplorer from "@/pages/CollegeExplorer";
import EngineeringCollegeExplorer from "@/pages/EngineeringCollegeExplorer";
import MedicalAdmission from "@/pages/details/MedicalAdmission";
import EngineeringAdmission from "@/pages/details/EngineeringAdmission";
import EngineeringAdmissionLogin from "@/pages/details/EngineeringAdmissionLogin";
import PrefAdminDashboard from "@/pages/details/PrefAdminDashboard";
import PrefCounselorWorkspace from "@/pages/details/PrefCounselorWorkspace";
import PrefStudentPortal from "@/pages/details/PrefStudentPortal";
import EngineeringTeam from "@/pages/details/EngineeringTeam";
import MedicalDocumentsChecklist from "@/pages/details/MedicalDocumentsChecklist/MedicalDocumentsChecklist";
import DocumentChecker from "@/pages/public/DocumentChecker";
import DownloadCutoffs from "@/pages/public/DownloadCutoffs";
import Neet2026CutoffExplorer from "@/pages/public/Neet2026CutoffExplorer";


import PublicAdminLogin from "@/pages/public/PublicAdminLogin";
import PublicAdminDashboard from "@/pages/public/PublicAdminDashboard";
import PublicAdminUserManagement from "@/pages/public/PublicAdminUserManagement";
import PublicAdminSubAdminManagement from "@/pages/public/PublicAdminSubAdminManagement";
import PublicAdminChat from "@/pages/public/PublicAdminChat";
import PublicAdminBroadcast from "@/pages/public/PublicAdminBroadcast";
import PublicAdminAudit from "@/pages/public/PublicAdminAudit";
import PublicAdminUpdates from "@/pages/public/PublicAdminUpdates";
import PublicAdminDocumentChecklist from "@/pages/public/PublicAdminDocumentChecklist";
import PublicAdminGuestStudents from "@/pages/public/PublicAdminGuestStudents";
import MedicalCounsellorStudents from "@/pages/public/MedicalCounsellorStudents";
import MedicalCounsellorWorkspace from "@/pages/public/MedicalCounsellorWorkspace";
import { ShieldX } from "lucide-react";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <DashboardLayout>{children}</DashboardLayout>;
}

function PublicProtectedRoute({ children }) {
  const { user, loading } = usePublicAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/public-login" replace />;
  return <PublicLayout>{children}</PublicLayout>;
}

function PublicAdminProtectedRoute({ children }) {
  const { admin, loading } = usePublicAdmin();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!admin) return <Navigate to="/portal-admin/login" replace />;
  return <PublicAdminLayout>{children}</PublicAdminLayout>;
}

function PublicAdminTabRoute({ children, tabId }) {
  const { admin, loading } = usePublicAdmin();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!admin) return <Navigate to="/portal-admin/login" replace />;
  const isMaster = admin.role === "PUBLIC_ADMIN";
  const allowedTabs = admin.allowed_tabs || [];
  if (!isMaster && tabId && !allowedTabs.includes(tabId)) {
    return (
      <PublicAdminLayout>
        <div className="flex flex-col items-center justify-center h-[70vh] text-center gap-4">
          <ShieldX className="h-16 w-16 text-rose-500 opacity-60" />
          <h2 className="text-2xl font-black text-white">Access Denied</h2>
          <p className="text-slate-400 max-w-xs">You don't have permission to view this section. Please contact your Master Admin.</p>
        </div>
      </PublicAdminLayout>
    );
  }
  return <PublicAdminLayout>{children}</PublicAdminLayout>;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const routes = { ADMIN: "/admin", BRANCH_HEAD: "/branch", STAFF: "/branch", STUDENT: "/student" };
  return <Navigate to={routes[user.role] || "/login"} replace />;
}

function App() {
  useEffect(() => {
    const handleOffline = () =>
      toast.warning("You are currently offline. Using cached data.", {
        id: "offline-status",
        duration: Infinity,
      });
    const handleOnline = () => {
      toast.dismiss("offline-status");
      toast.success("Back online!", { duration: 3000 });
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    // Security protections: Disable Right Click, Dragging, and Developer Tool hotkeys
    const disableContextMenu = (e) => e.preventDefault();
    const disableDrag = (e) => e.preventDefault();
    const disableShortcuts = (e) => {
      // F12
      if (e.key === "F12") {
        e.preventDefault();
      }
      // Ctrl+Shift+I / Ctrl+Shift+J / Ctrl+Shift+C
      if (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J" || e.key === "C" || e.key === "i" || e.key === "j" || e.key === "c")) {
        e.preventDefault();
      }
      // Ctrl+U (View Source) / Ctrl+S (Save Page)
      if (e.ctrlKey && (e.key === "U" || e.key === "S" || e.key === "u" || e.key === "s")) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", disableContextMenu);
    document.addEventListener("dragstart", disableDrag);
    document.addEventListener("keydown", disableShortcuts);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("contextmenu", disableContextMenu);
      document.removeEventListener("dragstart", disableDrag);
      document.removeEventListener("keydown", disableShortcuts);
    };
  }, []);

  return (
    <HelmetProvider>
      <ServerWakeupProvider>
        <AuthProvider>
          <PublicAuthProvider>
            <PublicAdminAuthProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/last-year-cutoff" element={<Navigate to="/public-login" replace />} />
                  <Route path="/poll" element={<Navigate to="/public-login" replace />} />
                  <Route path="/updates" element={<UpdatesPage />} />
                  <Route path="/medical-admission" element={<MedicalAdmission />} />
                  <Route path="/medical-documents-checklist" element={<MedicalDocumentsChecklist />} />
                  <Route path="/engineering-admission" element={<EngineeringAdmission />} />
                  <Route path="/engineering-admission/portal-login" element={<EngineeringAdmissionLogin />} />
                  <Route path="/engineering-admission/admin" element={<PrefAdminDashboard />} />
                  <Route path="/engineering-admission/workspace" element={<PrefCounselorWorkspace />} />
                  <Route path="/engineering-admission/my-lists" element={<PrefStudentPortal />} />
                  <Route path="/engineering-team" element={<EngineeringTeam />} />
                  <Route path="/medical-reg-details-form" element={<MhtCetForm />} />
                  <Route path="/mht-cet-registration" element={<Navigate to="/medical-reg-details-form" replace />} />
                  <Route path="/register-cet" element={<Navigate to="/medical-reg-details-form" replace />} />
                  <Route path="/mht-cet" element={<Navigate to="/medical-reg-details-form" replace />} />

                  <Route path="/public-login" element={<Navigate to="/guest-poll" replace />} />
                  <Route path="/public-register" element={<Navigate to="/guest-poll" replace />} />
                  <Route path="/guest-poll" element={<GuestLayout><GuestPoll /></GuestLayout>} />
                  <Route path="/guest-about" element={<GuestLayout><LandingPage isEmbedded={true} /></GuestLayout>} />
                  <Route path="/guest-chats" element={<GuestLayout><PublicChats /></GuestLayout>} />
                  <Route path="/guest-predictor" element={<GuestPredictorGate />} />
                  <Route path="/guest-cutoffs" element={<GuestLayout><PublicCutoffs /></GuestLayout>} />
                  <Route path="/guest-colleges" element={<GuestLayout><CollegeExplorer /></GuestLayout>} />
                  <Route path="/guest-documents" element={<GuestLayout><DocumentChecker /></GuestLayout>} />
                  <Route path="/download-cutoffs" element={<GuestLayout><DownloadCutoffs /></GuestLayout>} />
                  <Route path="/neet-2026-cutoff" element={<Neet2026CutoffExplorer />} />
                  <Route path="/guest-neet-2026-cutoff" element={<GuestLayout><Neet2026CutoffExplorer /></GuestLayout>} />


                  <Route path="/portal-admin/login" element={<PublicAdminLogin />} />
                  <Route path="/portal-admin" element={<PublicAdminTabRoute tabId="dashboard"><PublicAdminDashboard /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/users" element={<PublicAdminTabRoute tabId="users"><PublicAdminUserManagement /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/sub-admins" element={<PublicAdminProtectedRoute><PublicAdminSubAdminManagement /></PublicAdminProtectedRoute>} />
                  <Route path="/portal-admin/document" element={<PublicAdminTabRoute tabId="document"><PublicAdminDocumentChecklist /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/chats" element={<PublicAdminTabRoute tabId="chats"><PublicAdminChat /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/broadcast" element={<PublicAdminTabRoute tabId="broadcast"><PublicAdminBroadcast /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/updates" element={<PublicAdminTabRoute tabId="updates"><PublicAdminUpdates /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/audit" element={<PublicAdminTabRoute tabId="audit"><PublicAdminAudit /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/predictor" element={<PublicAdminTabRoute tabId="predictor"><PublicPredictor /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/guest-predictor" element={<PublicAdminTabRoute tabId="predictor"><div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%' }}><GuestPredictorGate isAdminMode={true} /></div></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/guest-students" element={<PublicAdminTabRoute tabId="predictor"><PublicAdminGuestStudents /></PublicAdminTabRoute>} />
                  <Route path="/portal-admin/medical-counsellor" element={<PublicAdminProtectedRoute><MedicalCounsellorStudents /></PublicAdminProtectedRoute>} />
                  <Route path="/portal-admin/medical-counsellor/:studentId" element={<PublicAdminProtectedRoute><MedicalCounsellorWorkspace /></PublicAdminProtectedRoute>} />

                  {/* Public PWA Routes */}
                  <Route path="/app/home" element={<PublicProtectedRoute><PublicHome /></PublicProtectedRoute>} />
                  <Route path="/app/about" element={<PublicProtectedRoute><LandingPage isEmbedded={true} /></PublicProtectedRoute>} />
                  <Route path="/app/chats" element={<PublicProtectedRoute><PublicChats /></PublicProtectedRoute>} />
                  <Route path="/app/predictor" element={<PublicProtectedRoute><PublicPredictor /></PublicProtectedRoute>} />
                  <Route path="/app/cutoffs" element={<PublicProtectedRoute><PublicCutoffs /></PublicProtectedRoute>} />
                  <Route path="/app/colleges" element={<PublicProtectedRoute><CollegeExplorer /></PublicProtectedRoute>} />
                  <Route path="/app/engineering-colleges" element={<EngineeringCollegeExplorer />} />
                  <Route path="/app/documents" element={<PublicProtectedRoute><DocumentChecker /></PublicProtectedRoute>} />
                  <Route path="/app/profile" element={<PublicProtectedRoute><PublicProfile /></PublicProtectedRoute>} />

                  <Route path="/student" element={<ProtectedRoute roles={["STUDENT"]}><StudentDashboard /></ProtectedRoute>} />
                  <Route path="/student/profile" element={<ProtectedRoute roles={["STUDENT"]}><StudentProfile /></ProtectedRoute>} />
                  <Route path="/student/documents" element={<ProtectedRoute roles={["STUDENT"]}><StudentDocuments /></ProtectedRoute>} />
                  <Route path="/student/chat" element={<ProtectedRoute roles={["STUDENT"]}><StudentChat /></ProtectedRoute>} />
                  <Route path="/branch" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><BranchDashboard /></ProtectedRoute>} />
                  <Route path="/branch/register" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><RegisterStudent /></ProtectedRoute>} />
                  <Route path="/branch/students" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><StudentList /></ProtectedRoute>} />
                  <Route path="/branch/verification" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><VerificationQueue /></ProtectedRoute>} />
                  <Route path="/branch/verification/:studentId" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><VerificationView /></ProtectedRoute>} />
                  <Route path="/branch/workplace" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><RegistrationWorkspace /></ProtectedRoute>} />
                  <Route path="/branch/audit" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><BranchAudit /></ProtectedRoute>} />
                  <Route path="/branch/staff" element={<ProtectedRoute roles={["BRANCH_HEAD"]}><StaffManagement /></ProtectedRoute>} />
                  <Route path="/branch/analytics" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><BranchAnalytics /></ProtectedRoute>} />
                  <Route path="/branch/expenses" element={<ProtectedRoute roles={["BRANCH_HEAD", "STAFF"]}><ExpenseManager /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute roles={["ADMIN"]}><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/branches" element={<ProtectedRoute roles={["ADMIN"]}><BranchManagement /></ProtectedRoute>} />
                  <Route path="/admin/analytics" element={<ProtectedRoute roles={["ADMIN"]}><Analytics /></ProtectedRoute>} />
                  <Route path="/admin/audit" element={<ProtectedRoute roles={["ADMIN"]}><AdminAudit /></ProtectedRoute>} />
                  <Route path="/admin/users" element={<ProtectedRoute roles={["ADMIN"]}><UserManagement /></ProtectedRoute>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
            </PublicAdminAuthProvider>
          </PublicAuthProvider>
          <Toaster theme="dark" position="top-right" richColors />
          <VercelAnalytics />
        </AuthProvider>
      </ServerWakeupProvider>
    </HelmetProvider>
  );
}

export default App;
